import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, Trash2, Truck } from "lucide-react";
import { gbp } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, 50 - subtotal);
  const pct = Math.min(100, (subtotal / 50) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 bg-background p-0 sm:max-w-md">
        <div className="flex items-center justify-between border-b border-border p-5">
          <h3 className="font-display text-lg font-semibold uppercase tracking-wide">Your Basket ({items.length})</h3>
          <button onClick={() => setOpen(false)} data-testid="close-cart"><X className="h-5 w-5" /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <p className="text-muted-foreground">Your basket is empty.</p>
            <button onClick={() => { setOpen(false); navigate("/shop"); }} className="bg-primary px-6 py-3 text-xs font-bold uppercase tracking-widest text-primary-foreground hover:brightness-125 transition-[filter]">Start Shopping</button>
          </div>
        ) : (
          <>
            <div className="border-b border-border p-4">
              <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
                <Truck className="h-4 w-4 text-accent" />
                {remaining > 0 ? <span>Spend <b className="text-foreground">{gbp(remaining)}</b> more for free delivery</span> : <span className="text-accent font-semibold">You've unlocked free UK delivery!</span>}
              </div>
              <div className="h-1.5 w-full bg-secondary"><div className="h-full bg-accent transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar divide-y divide-border">
              {items.map((i) => (
                <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-3 p-4">
                  <Link to={i.path || `/product/${i.slug}`} onClick={() => setOpen(false)} className="h-20 w-20 shrink-0 overflow-hidden bg-secondary">
                    <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                  </Link>
                  <div className="flex flex-1 flex-col">
                    <span className="overline">{i.brand}</span>
                    <Link to={i.path || `/product/${i.slug}`} onClick={() => setOpen(false)} className="text-sm font-medium leading-snug line-clamp-2 hover:text-accent transition-colors">{i.name}</Link>
                    <div className="mt-auto flex items-center justify-between pt-2">
                      <div className="flex items-center border border-border">
                        <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="grid h-7 w-7 place-items-center hover:text-accent"><Minus className="h-3 w-3" /></button>
                        <span className="w-7 text-center text-sm">{i.quantity}</span>
                        <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="grid h-7 w-7 place-items-center hover:text-accent"><Plus className="h-3 w-3" /></button>
                      </div>
                      <span className="font-display text-sm font-semibold">{gbp(i.price * i.quantity)}</span>
                    </div>
                  </div>
                  <button onClick={() => removeItem(i.product_id)} className="text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>

            <div className="border-t border-border p-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="font-display text-xl font-bold">{gbp(subtotal)}</span>
              </div>
              <button data-testid="checkout-button" onClick={() => { setOpen(false); navigate("/checkout"); }} className="mb-2 w-full bg-accent py-3.5 text-xs font-bold uppercase tracking-widest text-accent-foreground hover:brightness-110 transition-[filter]">Checkout</button>
              <button onClick={() => { setOpen(false); navigate("/cart"); }} className="w-full border border-border py-3 text-xs font-bold uppercase tracking-widest hover:border-accent transition-colors">View Basket</button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
